import { PaletteOptions, TypeText } from "@mui/material/styles/createPalette";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import oldTheme from "./oldTheme";
import { createTheme } from "@mui/material";

const fontFamily = '"articulat-cf", "Helvetica", "Arial", sans-serif';

const htmlFontSize = 16;
const fontWeightMedium = 500;
const fontWeightRegular = 400;

const typography: TypographyOptions = {
  fontFamily,
  htmlFontSize: htmlFontSize,
  fontSize: 16,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    fontFamily,
    fontSize: "80px",
    fontStyle: "normal",
    fontWeight: fontWeightRegular,
    fontFeatureSettings: `'clig' off, 'liga' off`,
    lineHeight: "116.7%",
    "@media (max-width: 960px)": {
      fontSize: "64px",
    },
    "@media (max-width: 600px)": {
      fontSize: "48px",
    },
  },
  h2: {
    fontFamily,
    fontSize: "64px",
    fontWeight: fontWeightRegular,
    lineHeight: "120%",
    "@media (max-width: 960px)": {
      fontSize: "48px",
    },
    "@media (max-width: 600px)": {
      fontSize: "36px",
    },
  },
  h3: {
    fontFamily,
    fontSize: "48px",
    fontWeight: fontWeightRegular,
    lineHeight: "122%",
    "@media (max-width: 960px)": {
      fontSize: "36px",
    },
    "@media (max-width: 600px)": {
      fontSize: "32px",
    },
  },
  h4: {
    fontFamily,
    fontSize: "36px",
    fontWeight: fontWeightRegular,
    lineHeight: "123.5%",
    "@media (max-width: 960px)": {
      fontSize: "32px",
    },
    "@media (max-width: 600px)": {
      fontSize: "28px",
    },
  },
  h5: {
    fontFamily,
    fontSize: "32px",
    fontWeight: fontWeightRegular,
    lineHeight: "130%",
    "@media (max-width: 960px)": {
      fontSize: "28px",
    },
    "@media (max-width: 600px)": {
      fontSize: "24px",
    },
  },
  h6: {
    fontFamily,
    fontSize: "28px",
    fontWeight: fontWeightRegular,
    lineHeight: "135%",
    "@media (max-width: 960px)": {
      fontSize: "24px",
    },
    "@media (max-width: 600px)": {
      fontSize: "22px",
    },
  },
  largeBody: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: "24px",
    lineHeight: "150%",
    letterSpacing: "0.5px",
    "@media (max-width: 960px)": {
      fontSize: "20px",
    },
    "@media (max-width: 600px)": {
      fontSize: "20px",
    },
  },
  body1: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: "20px",
    lineHeight: "150%",
    letterSpacing: "0.5px",
    "@media (max-width: 960px)": {
      fontSize: "18px",
    },
    "@media (max-width: 600px)": {
      fontSize: "18px",
    },
  },
  body2: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: "18px",
    lineHeight: "150%",
    letterSpacing: "0.5px",
    "@media (max-width: 960px)": {
      fontSize: "16px",
    },
    "@media (max-width: 600px)": {
      fontSize: "16px",
    },
  },
  subtitle1: {
    fontFamily,
    fontWeight: fontWeightMedium,
    fontSize: "24px",
    lineHeight: "140%",
    "@media (max-width: 960px)": {
      fontSize: "20px",
    },
    "@media (max-width: 600px)": {
      fontSize: "20px",
    },
  },
  subtitle2: {
    fontFamily,
    fontWeight: fontWeightMedium,
    fontSize: "20px",
    lineHeight: "140%",
    "@media (max-width: 960px)": {
      fontSize: "18px",
    },
    "@media (max-width: 600px)": {
      fontSize: "18px",
    },
  },
  caption: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: "16px",
    lineHeight: "150%",
    display: "inline-block",
    margin: 0,
    padding: 0,
    "@media (max-width: 960px)": {
      fontSize: "14px",
    },
    "@media (max-width: 600px)": {
      fontSize: "14px",
    },
  },
  overline: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: "16px",
    lineHeight: "100%",
    verticalAlign: "middle",
    textTransform: "uppercase",
    margin: 0,
    padding: 0,
    "@media (max-width: 960px)": {
      fontSize: "14px",
    },
    "@media (max-width: 600px)": {
      fontSize: "14px",
    },
  },
};

const basePalette: PaletteOptions = {
  primary: {
    light: "#2C2934",
    main: "#2C2934",
    dark: "#2C2934",
    contrastText: "#FFFFFF",
  },
  secondary: {
    light: "#004CF2",
    main: "#004CF2",
    dark: "#004CF2",
  },
  error: {
    light: "#D32F2F",
    main: "#D32F2F",
  },
  success: {
    light: "#E9FFDB",
    main: "#C0FF99",
    dark: "#6CD22D",
  },
};

const textPalette: TypeText = {
  primary: "#2C2934",
  secondary: "#59575D",
  disabled: "#D6D6D6",
};

const lightPalette = createTheme({
  palette: {
    mode: "light",
    ...basePalette,
    background: {
      default: "#FFFFFF",
      paper: "#F3F3F3",
    },
    text: textPalette,
  },
}).palette;

const darkPalette = createTheme({
  palette: {
    mode: "dark",
    ...basePalette,
    text: textPalette,
  },
}).palette;

const theme = (palette: PaletteOptions) =>
  createTheme({
    palette,
    typography,
    breakpoints: {
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1440,
        xl: 1920,
      },
    },
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            subtitle: "div",
            statement: "div",
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: "hover",
          color: "inherit",
        },
      },
      MuiButton: {
        defaultProps: {
          style: {
            borderRadius: 50,
            fontSize: "15px",
            paddingTop: "8px",
            paddingBottom: "8px",
          },
          disableElevation: true,
        },
      },
      MuiSwitch: {
        defaultProps: {
          sx: {
            "& .MuiSwitch-thumb": {
              border: "1px solid black",
            },
          },
        },
      },
    },
    customSpacing: {
      containerMaxWidth: { xs: "600px", sm: "1000px", md: "1800px" },
      marginSide: { xs: 2, sm: 3, md: 6 },
      marginTopLarge: { xs: 6, sm: 8, md: 12 },
      marginTopMedium: { xs: 4, sm: 6, md: 8 },
      marginTopSmall: { xs: 3, sm: 4, md: 6 },
      marginBottomLarge: { xs: 6, sm: 8, md: 12 },
      marginBottomMedium: { xs: 4, sm: 6, md: 8 },
      marginBottomSmall: { xs: 3, sm: 4, md: 6 },
      gutter: { xs: 2, sm: 3, md: 4 },
      verticalSpacing: { xs: 6, sm: 8, md: 12 },
      bodyTexBlockMaxWidth: { sm: "500px", md: "600px" },
      mobileContainerMaxWidth: { xs: "500px", sm: "500px", md: "500px" },
      titleMaxWidth: { xs: "500px", sm: "750px", md: "850px" },
      caseStudyTextBlockMaxWidth: { xs: "500px", sm: "1000px", md: "1200px" },
    },
  });

const themes = {
  light: theme(lightPalette),
  dark: theme(darkPalette),
  ...oldTheme,
};

export default themes;
